import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showError } from '../../../../redux/common/actions';
import { useReport } from '../ReportContext';
import { useFilters } from '../../../../components/Elements/Filters/FilterContext';
import { getFilterSubtitle } from '../utils';

/**
 * Custom hook for loading and processing chart data
 * @returns {Object} Object containing isLoading, chartData, subtitle, and invalidFiltersMessage
 */
export const useChartData = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [subtitle, setSubtitle] = useState('Show All');
  const [invalidFiltersMessage, setInvalidFiltersMessage] = useState(null);
  const { config, drilldownParams, fetchTrigger } = useReport();
  const { isReady, filterValues, buildUrlParams } = useFilters();

  useEffect(() => {
    if (!isReady || !config.fetchData) {
      setInvalidFiltersMessage('Please select all required filters.');
      setIsLoading(false);
      setChartData(null);
      return;
    }

    setIsLoading(true);
    setInvalidFiltersMessage(null);

    const debouncedFetch = async () => {
      setSubtitle(getFilterSubtitle(filterValues));
      
      try {
        const filterParams = buildUrlParams();
        
        const params = {
          ...filterParams,
          ...drilldownParams
        };
        
        console.log(`Sending API params: ${JSON.stringify(params)}`);
  
        const response = await config.fetchData(params);
        
        if (response?.status) {
          const transformedData = config.transformData ? 
            config.transformData(response, filterValues) : 
            response.data;
          
          console.log(transformedData);
          setChartData(transformedData);
          setInvalidFiltersMessage(null);
        } else if (response?.invalidFilters) {
          setInvalidFiltersMessage(response.message || 'Please select all required filters.');
          setChartData(null);
        } else {
          setChartData(null);
        }
      } catch (error) {
        dispatch(showError('Failed to load chart data'));
        console.error('Failed to load chart data:', error);
        setChartData(null);
        setInvalidFiltersMessage('Failed to load chart data.');
      } finally {
         setIsLoading(false);
      }
    };

    const timerId = setTimeout(() => {
      debouncedFetch();
    }, 100); 

    return () => {
      clearTimeout(timerId);
    };
  }, [filterValues, isReady, config, buildUrlParams, dispatch, drilldownParams, fetchTrigger]);

  return { isLoading, chartData, subtitle, invalidFiltersMessage };
}; 
